/**
 * GlobalSearchPage
 * 
 * Dedicated search page that searches across all exhibits.
 * Results are grouped by section with text highlighting.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Empty } from 'antd';
import { RootState } from '../../../store';
import SearchBar from './components/SearchBar';
import useGlobalSearch from './useGlobalSearch';
import FinancialStatementsSection from './sections/FinancialStatementsSection';
import ProcessHierarchySection from './sections/ProcessHierarchySection';
import RiskAssessmentSection from './sections/RiskAssessmentSection';
import ControlActivitiesSection from './sections/ControlActivitiesSection';
import GapsSection from './sections/GapsSection';
import COSOComplianceSection from './sections/COSOComplianceSection';
import CertificationSection from './sections/CertificationSection';
import AuditPlanSection from './sections/AuditPlanSection';

// ========================================
// Styled Components
// ========================================

const PageContainer = styled.div`
  padding: 32px;
  max-width: 1450px;
  margin: 0 auto;
  min-height: calc(100vh - 64px);
`;

const SearchSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`;

const ResultsSection = styled.div`
  margin-top: 24px;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: #F9FAFB;
  border-radius: 12px;
  margin-top: 32px;
`;

const EmptyStateText = styled.p`
  font-family: Inter;
  font-size: 16px;
  color: #6B7280;
  margin: 16px 0 0 0;
  text-align: center;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ========================================
// Component
// ========================================

const GlobalSearchPage: React.FC = () => {
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const annualReportId = selectedReport?.annualReportId;
  
  const {
    inputText,
    setInputText,
    searchText,
    isLoading,
    results,
    hasAnyResults,
  } = useGlobalSearch();
  
  // No annual report selected
  if (!annualReportId) {
    return (
      <PageContainer>
        <SearchSection>
          <SearchBar onSearch={setInputText} />
        </SearchSection>
        <EmptyStateWrapper>
          <EmptyStateText>Please select a fiscal year to search.</EmptyStateText>
        </EmptyStateWrapper>
      </PageContainer>
    );
  }
  
  // Show empty state only when search is done and no results
  const showEmptyState = !isLoading && !hasAnyResults && searchText.trim() !== '';
  
  // Check if we have data for each section
  const hasFinancialStatements = results.financialStatements && (
    results.financialStatements.assets.length > 0 ||
    results.financialStatements.liabilities.length > 0 ||
    results.financialStatements.equity.length > 0 ||
    results.financialStatements.revenue.length > 0 ||
    results.financialStatements.expenses.length > 0
  );
  
  const hasProcessHierarchy = results.processHierarchy && 
    results.processHierarchy.cycles.length > 0;
  
  const hasRiskAssessment = results.riskAssessment && 
    results.riskAssessment.assessments.length > 0;
  
  const hasControlActivities = results.controlActivities && 
    results.controlActivities.length > 0;
  
  const hasGaps = results.gaps && 
    results.gaps.length > 0;
  
  const hasCOSOCompliance = results.cosoCompliance && 
    (results.cosoCompliance.notes.length > 0 || results.cosoCompliance.documents.length > 0);
  
  const hasCertification = results.certification && 
    results.certification.processOwners.length > 0;
  
  const hasAuditPlan = results.auditPlan && (
    results.auditPlan.documents.length > 0 ||
    results.auditPlan.applications.length > 0 ||
    results.auditPlan.changeIndicators.length > 0 ||
    results.auditPlan.certificationMessages.length > 0
  );
  
  return (
    <PageContainer>
      <SearchSection>
        <SearchBar onSearch={setInputText} isLoading={isLoading && inputText.length > 0} />
      </SearchSection>
      
      {/* Initial state - no search yet */}
      {/* {!inputText && (
        <InitialStateWrapper>
          <InitialStateIcon>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </InitialStateIcon>
          <InitialStateText>
            Enter a search term to find content across all exhibits.
          </InitialStateText>
        </InitialStateWrapper>
      )} */}
      
      {/* Search results */}
      {inputText && (
        <ResultsSection>
          {/* Loading state - show only when loading and no results yet */}
          {isLoading && !hasAnyResults && (
            <LoadingWrapper>
              <LoadingSpinner>
                <div className="spinner" />
                <span className="text">Searching...</span>
              </LoadingSpinner>
            </LoadingWrapper>
          )}

          {/* EX2 - Financial Statements Section */}
          {hasFinancialStatements && results.financialStatements && (
            <FinancialStatementsSection
              data={results.financialStatements}
              searchText={searchText}
            />
          )}

          {/* EX3 - Process Hierarchy Section */}
          {hasProcessHierarchy && results.processHierarchy && (
            <ProcessHierarchySection
              data={results.processHierarchy}
              searchText={searchText}
            />
          )}

          {/* EX4 - Risk Assessment Section */}
          {hasRiskAssessment && results.riskAssessment && (
            <RiskAssessmentSection
              data={results.riskAssessment}
              searchText={searchText}
            />
          )}

          {/* EX5 - Control Activities Section */}
          {hasControlActivities && results.controlActivities && (
            <ControlActivitiesSection
              data={results.controlActivities}
              searchText={searchText}
            />
          )}

          {/* EX6 - Gaps Section */}
          {hasGaps && results.gaps && (
            <GapsSection
              data={results.gaps}
              searchText={searchText}
            />
          )}

          {/* EX7 - Certification Section */}
          {hasCertification && results.certification && (
            <CertificationSection
              data={results.certification}
              searchText={searchText}
            />
          )}

          {/* EX8 - COSO Compliance Section */}
          {hasCOSOCompliance && results.cosoCompliance && (
            <COSOComplianceSection
              data={results.cosoCompliance}
              searchText={searchText}
            />
          )}

          {/* EX9 - Audit Plan Section */}
          {hasAuditPlan && results.auditPlan && (
            <AuditPlanSection
              data={results.auditPlan}
              searchText={searchText}
            />
          )}

          {/* Empty state - only when all sections finished loading and no results */}
          {showEmptyState && (
            <EmptyStateWrapper>
              <Empty description={false} />
              <EmptyStateText>
                No results found for "{searchText}".
                <br />
                Try different keywords or check your spelling.
              </EmptyStateText>
            </EmptyStateWrapper>
          )}
        </ResultsSection>
      )}
    </PageContainer>
  );
};

export default GlobalSearchPage;
